import React, { useState } from 'react';

import { Icon, Text } from '@betterment/samba-react';
import { SambaAutoLink } from '@betterment/shared-components';
import { Box, Flex, Modal } from '@betterment/style-closet-react';

import CardLockUnlockController from './CardLockUnlockController';
import ContactUs from './ContactUs';
import { useCustomer } from './CustomerProvider';
import LockedCardModal from './LockedCardModal';
import { Props } from './Manage';
import ManageActionButton from './ManageActionButton';
import PendingSwitchSpendingAccount from './PendingSwitchSpendingAccount';
import SwitchSpendingAccount from './SwitchSpendingAccount';

import { trackClick } from '../../features/track';
import Card from '../../Shared/components/Card';
import RightCaret from '../../Shared/components/RightCaret';
import { urlBuilder } from '../../Shared/urlBuilder';
import { LockTypeCode } from '../data';
import { isUnlocked, isVerifiable, isVerified } from '../models/card';

function ActivateCardLink() {
  return (
    <ManageActionButton variant="tertiary">
      <Box py="4">
        <Flex justifyContent="space-between" alignItems="flex-start">
          <Flex alignItems="flex-start">
            <Box mr="3">
              <Icon name="Card" size="small" />
            </Box>
            <Text
              as="p"
              css={{
                margin: 'space.0',
                paddingBottom: 'space.50',
                textAlign: 'left',
              }}
            >
              Activate your debit card
            </Text>
          </Flex>
          <Box pb="2">
            <RightCaret label="Activate your card" />
          </Box>
        </Flex>
      </Box>
    </ManageActionButton>
  );
}

const ManageAccount: React.FC<React.PropsWithChildren<Props>> = ({
  account,
  card,
  cardPendingVerification,
  showOpenSpendingAccountChangeModal,
}) => {
  const { customer, refetch } = useCustomer();
  const hasCardPendingVerification = isVerifiable(card) || isVerifiable(cardPendingVerification);
  const isCardVerified = isVerified(card);
  const isCardUnlocked = isUnlocked(card);
  const isCardSystemLocked = card.lockTypeCode === LockTypeCode.SystemLocked;
  const shouldShowActivateCardLink = !isCardSystemLocked && hasCardPendingVerification;

  const hasMultipleAccounts = customer.accounts.length > 1;
  const shouldShowJointAccountCreationLink = account.sentInvitations.length === 0 && !account.isJointAccount;
  const currentAccountUrl = urlBuilder.getAppCheckingSummary.absolute({ accountId: account.id });

  const [shouldShowSpendingAccountChangeModal, setShouldShowSpendingAccountChangeModal] = useState(
    showOpenSpendingAccountChangeModal
  );
  const closeModal = async () => {
    await refetch();
    setShouldShowSpendingAccountChangeModal(false);
  };

  const [shouldShowCardLockedModal, setShouldShowCardLockedModal] = useState(false);

  return (
    <Box mb="6">
      <Card>
        <Card.Slot name="header">
          <Text
            as="p"
            css={{
              fontSize: 'fontSize.200',
              fontWeight: 'medium',
              margin: 'space.0',
            }}
          >
            Manage account
          </Text>
        </Card.Slot>
        <Card.Slot name="body">
          <Box mb={-6}>
            {isCardVerified && (
              <>
                <CardLockUnlockController card={card} account={account} />
                <SambaAutoLink
                  to={urlBuilder.getAppCheckingPinChange.absolute({ return_to: currentAccountUrl })}
                  onClick={() => trackClick({ name: 'ChangePin', module: 'ManageCheckingAccount' })}
                >
                  <ManageActionButton variant="tertiary">
                    <Box py="4">
                      <Flex justifyContent="space-between" alignItems="flex-start">
                        <Flex alignItems="flex-start">
                          <Box mr="3">
                            <Icon name="Pin" size="small" />
                          </Box>
                          <Text
                            as="p"
                            aria-label="Change pin"
                            css={{
                              margin: 'space.0',
                              paddingBottom: 'space.50',
                              textAlign: 'left',
                            }}
                          >
                            Change debit card PIN
                          </Text>
                        </Flex>
                        <Box pb="2">
                          <RightCaret label="Change PIN" />
                        </Box>
                      </Flex>
                    </Box>
                  </ManageActionButton>
                </SambaAutoLink>
              </>
            )}
            {shouldShowActivateCardLink &&
              (isCardUnlocked ? (
                <SambaAutoLink
                  to={urlBuilder.getAppCheckingCardSetup.absolute({ return_to: currentAccountUrl })}
                  onClick={() => trackClick({ name: 'ActivateCard', module: 'ManageCheckingAccount' })}
                >
                  <ActivateCardLink />
                </SambaAutoLink>
              ) : (
                <Box
                  onClick={() => {
                    trackClick({ name: 'ActivateCard', module: 'ManageCheckingAccount' });
                    setShouldShowCardLockedModal(true);
                  }}
                >
                  {shouldShowCardLockedModal && (
                    <LockedCardModal closeHandler={() => setShouldShowCardLockedModal(false)} />
                  )}
                  <ActivateCardLink />
                </Box>
              ))}
            {hasMultipleAccounts && isCardUnlocked && (
              <ManageActionButton variant="tertiary" onClick={() => setShouldShowSpendingAccountChangeModal(true)}>
                <Flex justifyContent="space-between" alignItems="flex-start" py="4">
                  <Flex alignItems="flex-start">
                    <Box mr="3">
                      <Icon name="Card" size="small" />
                    </Box>
                    <Text
                      as="p"
                      css={{
                        margin: 'space.0',
                        paddingBottom: 'space.50',
                        textAlign: 'left',
                      }}
                    >
                      Switch spending account
                    </Text>
                  </Flex>
                  <Box pb="2">
                    <RightCaret label="Switch spending account" />
                  </Box>
                </Flex>
              </ManageActionButton>
            )}
            {shouldShowJointAccountCreationLink && (
              <SambaAutoLink
                to={urlBuilder.getAppCheckingJointAccounts.absolute({
                  accountId: account.id,
                  return_to: currentAccountUrl,
                })}
                onClick={() => trackClick({ name: 'AddSomeoneToJCAccount', module: 'ManageCheckingAccount' })}
              >
                <ManageActionButton variant="tertiary">
                  <Flex justifyContent="space-between" alignItems="flex-start" py="4">
                    <Flex alignItems="flex-start">
                      <Box mr="3">
                        <Icon name="People" size="small" />
                      </Box>
                      <Text
                        as="p"
                        css={{
                          margin: 'space.0',
                          paddingBottom: 'space.50',
                          textAlign: 'left',
                        }}
                      >
                        Add someone to account
                      </Text>
                    </Flex>
                    <Box pb="2">
                      <RightCaret label="New joint account" />
                    </Box>
                  </Flex>
                </ManageActionButton>
              </SambaAutoLink>
            )}
          </Box>
          {shouldShowSpendingAccountChangeModal && (
            <Modal contentWidth={['100%', '600px']} closeHandler={closeModal} label="switch spending account modal">
              <Box borderRadius="4px" border="1px solid" borderColor="bmtGrey20" p="9">
                {card.hasActiveSpendingAccountChangeRequest ? (
                  <PendingSwitchSpendingAccount onClose={closeModal} />
                ) : (
                  <SwitchSpendingAccount customer={customer} onClose={closeModal} />
                )}
              </Box>
            </Modal>
          )}
        </Card.Slot>
        <Card.Slot name="footer">
          <Box>
            <ContactUs />
          </Box>
        </Card.Slot>
      </Card>
    </Box>
  );
};

export default ManageAccount;
